import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import { BellIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useLayoutEffect, useMemo, useRef, useState } from 'react'

import { getTripDate } from '../../../api/trip-dates/get-trip-date'
import { TripStepTypeEnum } from '../../../lib/types/enums/trip-bookings/trip-step-type.enum'
import { TripDateProperties } from '../../../lib/types/models/trip-booking/properties.trip-date'
import { TripBookingTypes } from '../../../lib/types/models/trip-booking/trip-booking'
import { TripBookingDispatchPlaceIndividualTypeEnum } from '../../../lib/types/models/trip-booking/trip-date'
import { TripBookingDispatchPlace } from '../../../lib/types/models/trip-booking-dispatch-places/trip-booking-dispatch-place'
import { DispatchTable, DispatchTableRef } from './dispatch-table'
import { TripBookingDetailedSection } from './trip-booking-details'
import { TripDateVariantType } from './trip-date.utils'

type DispatchRetainTablesProps = {
  // trip date id to fetch enrollments from
  tripDateId: number
  // version of the table to display
  version: TripDateVariantType
  // hide the dispatch tables
  hideDispatchTables?: boolean
  // hide the retain tables
  hideRetainTables?: boolean
  // on success callback
  onSuccess?: () => void
}

export function DispatchRetainTables({
  tripDateId,
  version,
  hideDispatchTables = false,
  hideRetainTables = false,
  onSuccess
}: DispatchRetainTablesProps) {
  const { data: tripDate } = useQuery({
    queryKey: ['trip-date', tripDateId],
    queryFn: () => getTripDate(tripDateId),
    enabled: !hideRetainTables,
    staleTime: 0
  })

  const dispatchEnrollmentsRef = useRef<DispatchTableRef>(null)
  const dispatchStaffsRef = useRef<DispatchTableRef>(null)
  const retainEnrollmentsRef = useRef<DispatchTableRef>(null)
  const retainStaffsRef = useRef<DispatchTableRef>(null)

  const [tripBooking, setTripBooking] = useState<TripBookingTypes.Partial | undefined>()
  const [dispatchEnrollmentsData, setDispatchEnrollmentsData] = useState<
    TripBookingDispatchPlace[]
  >([])

  const tripBookings = useMemo(
    () => TripDateProperties.getTripBookingsByDirectionality(tripDate),
    [tripDate]
  )

  useLayoutEffect(() => {
    if (!tripBookings?.length) return
    setTripBooking(
      (current) => tripBookings.find((tb) => tb.id === current?.id) ?? tripBookings.at(0)
    )
  }, [tripBookings])

  return (
    <div className={'flex flex-col gap-4'}>
      {!hideDispatchTables && (
        <Box className="bg-primary-light rounded-xl p-5 flex flex-col gap-5 shadow-[0px_0px_17.4px_0px_#2E8EA066]">
          <div className={'flex flex-row gap-2 justify-between items-center'}>
            <Typography variant={'sub-heading'} color={tailwindColors.primary.DEFAULT}>
              À dispatcher
            </Typography>

            {/* display error when we have dispatched enrollments */}
            {dispatchEnrollmentsData && dispatchEnrollmentsData.length > 0 && (
              <Badge
                variant={'danger'}
                className={'py-4'}
                iconPosition={'start'}
                textVariant={'secondary-body'}
                borderColor={tailwindColors.red.light.hover}
                icon={<BellIcon className={'w-6 h-6'} color={tailwindColors.red.DEFAULT} />}
                text={'Actions requises : pax à dispatcher ci-dessous'}
              />
            )}
          </div>

          {/* enrollments to dispatch table */}
          <Box className={'rounded-xl flex flex-col gap-3 shadow-[0px_0px_16.9px_0px_#2E8EA040]'}>
            <DispatchTable
              ref={dispatchEnrollmentsRef}
              title="Liste des jeunes"
              version={version}
              variant={'dispatch'}
              tripDateId={tripDateId}
              individualTypes={[TripBookingDispatchPlaceIndividualTypeEnum.ENROLLMENT]}
              onDataChange={setDispatchEnrollmentsData}
              onSuccess={() => {
                dispatchEnrollmentsRef.current?.refetch()
                retainEnrollmentsRef.current?.refetch()
                onSuccess?.()
              }}
            />
          </Box>

          {/* staffs to dispatch table */}
          <Box className={'rounded-xl flex flex-col gap-3 shadow-[0px_0px_16.9px_0px_#2E8EA040]'}>
            <DispatchTable
              ref={dispatchStaffsRef}
              title="Liste des encadrants"
              version={version}
              variant={'dispatch'}
              tripDateId={tripDateId}
              individualTypes={[
                TripBookingDispatchPlaceIndividualTypeEnum.AFFECTED_STAFF,
                TripBookingDispatchPlaceIndividualTypeEnum.MISSION_TO_STAFF,
                TripBookingDispatchPlaceIndividualTypeEnum.MISSION_TO_EXTERNAL_STAFF,
                TripBookingDispatchPlaceIndividualTypeEnum.MISSION_TO_LEC_STAFF
              ]}
              onSuccess={() => {
                dispatchStaffsRef.current?.refetch()
                retainStaffsRef.current?.refetch()
                onSuccess?.()
              }}
            />
          </Box>
        </Box>
      )}

      {!hideRetainTables && (
        <Box className={'p-4 rounded-xl flex flex-col gap-4 border border-grey-light'}>
          <Typography variant={'sub-heading'} color={tailwindColors.primary.DEFAULT}>
            Dispatchés
          </Typography>

          {/* display the trip bookings details */}
          {tripBookings && !!tripDate && (
            <TripBookingDetailedSection
              tripType={tripDate?.trip.type}
              tripBookings={tripBookings}
              directionality={tripDate?.directionality ?? TripStepTypeEnum.INBOUND}
              onTripBookingChange={setTripBooking}
            />
          )}

          {/* dispatched students table */}
          <DispatchTable
            ref={retainEnrollmentsRef}
            title="Liste des jeunes"
            version={version}
            variant={'retain'}
            tripDateId={tripDateId}
            tripBookingId={tripBooking?.id}
            individualTypes={[TripBookingDispatchPlaceIndividualTypeEnum.ENROLLMENT]}
            onSuccess={() => {
              dispatchEnrollmentsRef.current?.refetch()
              retainEnrollmentsRef.current?.refetch()
              onSuccess?.()
            }}
          />

          {/* dispatched staffs table */}
          <DispatchTable
            ref={retainStaffsRef}
            title="Liste des encadrants"
            version={version}
            variant={'retain'}
            tripDateId={tripDateId}
            tripBookingId={tripBooking?.id}
            individualTypes={[
              TripBookingDispatchPlaceIndividualTypeEnum.AFFECTED_STAFF,
              TripBookingDispatchPlaceIndividualTypeEnum.MISSION_TO_STAFF,
              TripBookingDispatchPlaceIndividualTypeEnum.MISSION_TO_EXTERNAL_STAFF,
              TripBookingDispatchPlaceIndividualTypeEnum.MISSION_TO_LEC_STAFF
            ]}
            onSuccess={() => {
              dispatchStaffsRef.current?.refetch()
              retainStaffsRef.current?.refetch()
              onSuccess?.()
            }}
          />
        </Box>
      )}
    </div>
  )
}
